import { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../../config';
import { toastSuccess, toastError, errorMessage } from '../../../lib/toast';
import { useAuth } from '../../../context/AuthContext';

const ERR_MAP = {
  unknown_flag: 'That feature is not recognised.',
  invalid_value: 'Invalid value.',
  update_failed: 'Could not save. Try again.',
};

export default function FeatureFlagsPanel() {
  const [flags, setFlags] = useState({});
  const [registry, setRegistry] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [saving, setSaving] = useState(null);
  const { refreshFeatures } = useAuth();

  useEffect(() => {
    let active = true;
    axios
      .get(`${API_BASE_URL}/api/feature-flags`, { withCredentials: true })
      .then((r) => { if (active) { setFlags(r.data.flags || {}); setRegistry(r.data.registry || {}); } })
      .catch(() => { if (active) setError(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const toggle = async (key) => {
    const next = flags[key] === false;
    setFlags((f) => ({ ...f, [key]: next }));
    setSaving(key);
    try {
      const r = await axios.put(
        `${API_BASE_URL}/api/admin/feature-flags/${key}`,
        { enabled: next },
        { withCredentials: true }
      );
      setFlags(r.data.flags || {});
      if (refreshFeatures) refreshFeatures();
      toastSuccess(`${registry[key]?.label || key} ${next ? 'enabled' : 'disabled'}.`);
    } catch (err) {
      setFlags((f) => ({ ...f, [key]: !next }));
      toastError(errorMessage(err, 'Could not update the feature.', ERR_MAP));
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <div className="admin-settings-state">Loading settings…</div>;
  if (error) return <div className="admin-settings-state">Could not load settings.</div>;

  const keys = Object.keys(registry);
  const enabled = keys.filter((k) => flags[k] !== false).length;

  return (
    <div className="admin-settings">
      <p className="set-intro">Toggle features on or off for the whole organization.</p>
      <span className="set-count">{enabled} of {keys.length} enabled</span>
      <ul className="set-flag-grid">
        {keys.map((key) => {
          const meta = registry[key] || {};
          const on = flags[key] !== false;
          return (
            <li key={key} className="set-flag-card">
              <div className="set-flag-card__text">
                <span className="admin-settings-label">{meta.label || key}</span>
                <span className="admin-settings-desc">{meta.description}</span>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={on}
                disabled={saving === key}
                className={`admin-toggle ${on ? 'admin-toggle--on' : ''}`}
                onClick={() => toggle(key)}
              >
                <span className="admin-toggle-knob" />
                <span className="admin-toggle-text">{on ? 'On' : 'Off'}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
